import { useEffect, useState } from 'react';
import { api, ApiError, type Checklist, type Restaurant, type VerifyResult } from './api';

/**
 * Connecting a restaurant's Cloudflare account.
 *
 * The screen has to carry a fact that is easy to get wrong and expensive to
 * get wrong: **the Cloudflare account must be created by the restaurant, in
 * the restaurant's name.** Cloudflare's terms forbid signing up on behalf of a
 * third party, and the penalty is account termination — which for a POS means
 * a restaurant going dark mid-service with its bill history inside the
 * terminated account.
 *
 * So the reseller guides the owner through creating it, and then operates it
 * with a scoped token. That is ordinary managed-service practice, and it is
 * also better on exit: when a reseller leaves, the restaurant keeps its
 * account and only the token rotates.
 */
export function ConnectPanel({
  restaurant,
  onConnected,
}: {
  restaurant: Restaurant;
  onConnected: () => Promise<void>;
}) {
  const [checklist, setChecklist] = useState<Checklist | null>(null);
  const [accountId, setAccountId] = useState(restaurant.cf_account_id ?? '');
  const [token, setToken] = useState('');
  const [result, setResult] = useState<VerifyResult | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(!restaurant.has_token);

  // `has_token` arrives from SQLite as 0 or 1, not a boolean. Rendering
  // `{restaurant.has_token && …}` puts a literal 0 on the page, because React
  // renders the number rather than treating it as falsy-and-skip.
  const connected = restaurant.has_token > 0;

  useEffect(() => {
    void api.checklist().then(setChecklist).catch(() => {});
  }, []);

  useEffect(() => {
    setAccountId(restaurant.cf_account_id ?? '');
    setToken('');
    setResult(null);
    setOpen(!restaurant.has_token);
  }, [restaurant.id, restaurant.has_token, restaurant.cf_account_id]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError('');
    setResult(null);
    try {
      const r = await api.connect(restaurant.id, accountId.trim(), token.trim());
      setResult(r);
      if (r.ok) {
        // Clear it from component state the moment it is accepted. The token
        // is already sealed server-side and there is no endpoint that returns
        // it — leaving a copy in a React state tree serves nothing.
        setToken('');
        await onConnected();
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not reach the portal');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card connect">
      <div className="pane-head">
        <h2>Cloudflare account</h2>
        <span className="spacer" />
        {connected && (
          <button className="btn sm" onClick={() => setOpen((v) => !v)}>
            {open ? 'Cancel' : 'Replace token'}
          </button>
        )}
      </div>

      {connected && !open && (
        <div className="banner ok">
          Connected to <span className="mono">{restaurant.cf_account_id}</span> with a token
          ending <span className="mono">••••{restaurant.token_last4}</span>.
        </div>
      )}

      {open && (
        <>
          <div className="banner warn">
            <strong>The restaurant must create the Cloudflare account themselves</strong>, in
            their own name and with their own email. Walk them through it — Cloudflare's terms
            do not permit signing up on someone else's behalf, and an account created the wrong
            way can be terminated with the restaurant's data inside it.
          </div>

          {checklist && (
            <>
              <ol className="steps">
                {checklist.steps.map((s, i) => (
                  <li key={i}>{s}</li>
                ))}
              </ol>

              <table className="perms">
                <thead>
                  <tr>
                    <th>Scope</th>
                    <th>Permission</th>
                    <th>Level</th>
                    <th>Why</th>
                  </tr>
                </thead>
                <tbody>
                  {checklist.permissions.map((p) => (
                    <tr key={p.name}>
                      <td>{p.scope}</td>
                      <td className="mono">{p.name}</td>
                      <td>{p.level}</td>
                      <td className="hint">{p.why}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <a
                className="btn primary"
                href={checklist.createUrl}
                target="_blank"
                rel="noreferrer"
                style={{ display: 'inline-block', textDecoration: 'none', marginTop: 4 }}
              >
                Open Cloudflare with these permissions →
              </a>
              <p className="hint">
                The four permissions above arrive pre-selected. Confirm all four are
                there before creating the token — if any is missing, add it by hand.{' '}
                <a href={checklist.plainCreateUrl} target="_blank" rel="noreferrer">
                  Plain token page
                </a>
                .
              </p>
            </>
          )}

          <form onSubmit={submit}>
            <label htmlFor="tok">API token</label>
            <input
              id="tok"
              type="password"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="shown only once when created"
              spellCheck={false}
              required
            />
            <div className="hint">
              Stored encrypted and never shown again. There is no way to read it back — if it is
              lost, create a new one and replace it here.
            </div>

            <label htmlFor="acct">Account ID — optional</label>
            <input
              id="acct"
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
              placeholder="leave blank — we read it from the token"
              spellCheck={false}
            />
            <div className="hint">
              <strong>Usually leave this blank.</strong> The token already says which
              account it belongs to, so we read it from there — and if it turns out to
              reach more than one, you get a list to pick from.
              <br />
              <br />
              If you do need it: sign in to Cloudflare and look at the address bar. It is
              the 32-character code in the URL —{' '}
              <span className="mono">
                dash.cloudflare.com/<strong>a1b2c3…</strong>/workers
              </span>
              . It is also on the right-hand side of the account home page, with a copy
              button.
            </div>

            {error && <div className="banner bad">{error}</div>}

            <div className="actions">
              <button className="btn primary" disabled={busy}>
                {busy ? 'Checking with Cloudflare…' : 'Verify and connect'}
              </button>
            </div>
          </form>
        </>
      )}

      {result && (
        <VerifyReport
          result={result}
          onPick={(id) => {
            // Fill the field and reopen the form rather than connecting
            // straight away: the operator should see which account they chose
            // before a customer credential is stored against it.
            setAccountId(id);
            setResult(null);
            setOpen(true);
          }}
        />
      )}
    </section>
  );
}

/**
 * What the token could and could not do.
 *
 * Showing each capability separately rather than one pass/fail is the point:
 * the common failure is a valid token scoped to the wrong account, and
 * "verification failed" sends someone hunting in the wrong place.
 */
function VerifyReport({
  result,
  onPick,
}: {
  result: VerifyResult;
  onPick?: (id: string) => void;
}) {
  if (result.fatal) {
    return (
      <div style={{ marginTop: 12 }}>
        <div className="banner bad">{result.fatal}</div>
        {result.chooseFrom && (
          <ul className="list" style={{ marginTop: 8 }}>
            {result.chooseFrom.map((a) => (
              <li key={a.id}>
                <button className="row" type="button" onClick={() => onPick?.(a.id)}>
                  <span className="name">{a.name}</span>
                  <span className="meta">{a.id}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  return (
    <div style={{ marginTop: 12 }}>
      <div className={`banner ${result.ok ? 'ok' : 'bad'}`}>
        {result.ok
          ? `Connected to ${result.accountName || 'the account'}. Ready to deploy.`
          : 'The token is missing permissions. Add them and paste a new token.'}
      </div>
      <ul className="caps">
        {result.capabilities.map((c) => (
          <li key={c.id} className={c.ok ? 'ok' : 'bad'}>
            <span className="tick">{c.ok ? '✓' : '✕'}</span>
            <span className="grow">{c.label}</span>
            {c.detail && <span className="hint">{c.detail}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}
